/**
 * Shared validation for marketing request submissions.
 * Used by both frontend (RequestAccess.tsx) and Cloud Function (submitMarketingRequest).
 */

export interface MarketingRequestInput {
  name: string;
  email: string;
  organizationName?: string | null;
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
  normalized?: {
    name: string;
    email: string;
    organizationName: string | null;
  };
}

// Email format validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates and normalizes marketing request input.
 * Returns normalized values if valid, or an error message if invalid.
 */
export function validateMarketingRequest(input: MarketingRequestInput): ValidationResult {
  const { name, email, organizationName } = input;

  // Trim inputs
  const trimmedName = (name || '').trim();
  const trimmedEmail = (email || '').trim().toLowerCase();
  const trimmedOrgName = organizationName?.trim() || null;

  // Validate name
  if (!trimmedName) {
    return { valid: false, error: 'Name is required' };
  }

  // Validate email presence
  if (!trimmedEmail) {
    return { valid: false, error: 'Email is required' };
  }

  // Validate email format
  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return { valid: false, error: 'Invalid email format' };
  }

  return {
    valid: true,
    normalized: {
      name: trimmedName,
      email: trimmedEmail,
      organizationName: trimmedOrgName,
    },
  };
}
